import React, { useEffect, useState, useCallback } from "react";
import { Card, Table, Button, Modal, Form, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { AiOutlinePlus, AiOutlineEdit, AiOutlineDelete } from "react-icons/ai";

const emptyFilter = () => ({ column: "", value: "" });
const emptyVatRule = () => ({ column: "", value: "", vat_rate: "" });

const emptyForm = {
  name: "",
  sig_header_1: "",
  sig_header_2: "",
  sig_header_3: "",
  identifier_column: "",
  value_column: "",
  row_filters: [],
  vat_rules: [],
};

const parseRulesFromRow = (row) => ({
  name: row.name || "",
  sig_header_1: row.sig_header_1 || "",
  sig_header_2: row.sig_header_2 || "",
  sig_header_3: row.sig_header_3 || "",
  identifier_column: row.identifier_column || "",
  value_column: row.value_column || "",
  row_filters: Array.isArray(row.row_filters)
    ? row.row_filters.map((f) => ({ column: f.column || "", value: f.value || "" }))
    : [],
  vat_rules: Array.isArray(row.vat_rules)
    ? row.vat_rules.map((r) => ({
        column: r.column || "",
        value: r.value || "",
        vat_rate: r.vat_rate != null ? String(r.vat_rate) : "",
      }))
    : [],
});

const ImportTemplates = () => {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalShow, setModalShow] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const headers = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get("/api/import-templates", { headers: headers() });
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.error || t("importTemplatesLoadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalShow(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm(parseRulesFromRow(row));
    setModalShow(true);
  };

  const addFilter = () => {
    setForm((f) => ({ ...f, row_filters: [...f.row_filters, emptyFilter()] }));
  };

  const updateFilter = (index, field, value) => {
    setForm((f) => ({
      ...f,
      row_filters: f.row_filters.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }));
  };

  const removeFilter = (index) => {
    setForm((f) => ({ ...f, row_filters: f.row_filters.filter((_, i) => i !== index) }));
  };

  const addVatRule = () => {
    setForm((f) => ({ ...f, vat_rules: [...f.vat_rules, emptyVatRule()] }));
  };

  const updateVatRule = (index, field, value) => {
    setForm((f) => ({
      ...f,
      vat_rules: f.vat_rules.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }));
  };

  const removeVatRule = (index) => {
    setForm((f) => ({ ...f, vat_rules: f.vat_rules.filter((_, i) => i !== index) }));
  };

  const buildPayload = () => ({
    name: form.name,
    sig_header_1: form.sig_header_1,
    sig_header_2: form.sig_header_2,
    sig_header_3: form.sig_header_3,
    identifier_column: form.identifier_column,
    value_column: form.value_column,
    row_filters: form.row_filters
      .map((f) => ({ column: f.column.trim(), value: f.value.trim() }))
      .filter((f) => f.column && f.value),
    vat_rules: form.vat_rules
      .map((r) => ({
        column: r.column.trim(),
        value: r.value.trim(),
        vat_rate: parseFloat(String(r.vat_rate).replace(",", ".")),
      }))
      .filter((r) => r.column && r.value && Number.isFinite(r.vat_rate) && r.vat_rate > 0),
  });

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = buildPayload();
      if (editingId) {
        await axios.put(`/api/import-templates/${editingId}`, payload, { headers: headers() });
      } else {
        await axios.post("/api/import-templates", payload, { headers: headers() });
      }
      setModalShow(false);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || t("importTemplatesSaveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(t("importTemplateDeleteConfirm"))) return;
    try {
      await axios.delete(`/api/import-templates/${row.id}`, { headers: headers() });
      await load();
    } catch (err) {
      alert(err.response?.data?.error || t("importTemplatesDeleteError"));
    }
  };

  const rulesSummary = (filters, vatRules) => {
    const f = Array.isArray(filters) ? filters.length : 0;
    const v = Array.isArray(vatRules) ? vatRules.length : 0;
    if (f === 0 && v === 0) return "—";
    const parts = [];
    if (f > 0) parts.push(t("importTemplateFiltersCount", { count: f }));
    if (v > 0) parts.push(t("importTemplateVatRulesCount", { count: v }));
    return parts.join(" · ");
  };

  return (
    <div className="mb-4">
      <h2 className="mb-2">{t("importTemplatesTitle")}</h2>
      <p className="text-muted small mb-4">{t("importTemplatesSubtitle")}</p>

      <Card className="mb-3">
        <Card.Body className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <span className="text-muted small">{t("importTemplatesHint")}</span>
          <Button variant="primary" size="sm" onClick={openNew}>
            <AiOutlinePlus className="me-1" />
            {t("importTemplateAdd")}
          </Button>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="p-4 text-center">
              <Spinner animation="border" size="sm" />
            </div>
          ) : (
            <Table responsive striped hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>{t("importTemplateName")}</th>
                  <th>{t("importTemplateSigShort")} 1</th>
                  <th>{t("importTemplateSigShort")} 2</th>
                  <th>{t("importTemplateSigShort")} 3</th>
                  <th>{t("importTemplateIdentifierCol")}</th>
                  <th>{t("importTemplateValueCol")}</th>
                  <th>{t("importTemplateRulesCol")}</th>
                  <th style={{ width: 120 }} />
                </tr>
              </thead>
              <tbody>
                {list.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-4">
                      {t("importTemplatesEmpty")}
                    </td>
                  </tr>
                ) : (
                  list.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <code>{row.name}</code>
                        <div className="small text-muted">{row.name_normalized}</div>
                      </td>
                      <td className="small">{row.sig_header_1}</td>
                      <td className="small">{row.sig_header_2}</td>
                      <td className="small">{row.sig_header_3}</td>
                      <td className="small">{row.identifier_column}</td>
                      <td className="small">{row.value_column}</td>
                      <td className="small text-muted">{rulesSummary(row.row_filters, row.vat_rules)}</td>
                      <td>
                        <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => openEdit(row)}>
                          <AiOutlineEdit />
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={() => handleDelete(row)}>
                          <AiOutlineDelete />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={modalShow}
        onHide={() => !saving && setModalShow(false)}
        size="lg"
        scrollable
        dialogClassName="import-template-modal"
      >
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton className="flex-shrink-0">
            <Modal.Title>{editingId ? t("importTemplateEdit") : t("importTemplateAdd")}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="import-template-modal__body">
            <Form.Group className="mb-3">
              <Form.Label>{t("importTemplateName")}</Form.Label>
              <Form.Control
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
                placeholder={t("importTemplateNamePlaceholder")}
              />
              <Form.Text className="text-muted">{t("importTemplateNameHint")}</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("importTemplateSigHeaders")}</Form.Label>
              <Form.Control
                className="mb-2"
                value={form.sig_header_1}
                onChange={(e) => setForm((f) => ({ ...f, sig_header_1: e.target.value }))}
                required
                placeholder={t("importTemplateSigPlaceholder")}
              />
              <Form.Control
                className="mb-2"
                value={form.sig_header_2}
                onChange={(e) => setForm((f) => ({ ...f, sig_header_2: e.target.value }))}
                required
              />
              <Form.Control
                value={form.sig_header_3}
                onChange={(e) => setForm((f) => ({ ...f, sig_header_3: e.target.value }))}
                required
              />
              <Form.Text className="text-muted">{t("importTemplateSigHint")}</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("importTemplateIdentifierCol")}</Form.Label>
              <Form.Control
                value={form.identifier_column}
                onChange={(e) => setForm((f) => ({ ...f, identifier_column: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("importTemplateValueCol")}</Form.Label>
              <Form.Control
                value={form.value_column}
                onChange={(e) => setForm((f) => ({ ...f, value_column: e.target.value }))}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0">{t("importTemplateRowFilters")}</Form.Label>
                <Button variant="outline-secondary" size="sm" type="button" onClick={addFilter}>
                  <AiOutlinePlus className="me-1" />
                  {t("importTemplateAddRule")}
                </Button>
              </div>
              <Form.Text className="text-muted d-block mb-2">{t("importTemplateRowFiltersHint")}</Form.Text>
              {form.row_filters.length === 0 ? (
                <div className="text-muted small">{t("importTemplateNoRules")}</div>
              ) : (
                form.row_filters.map((filter, index) => (
                  <div key={`filter-${index}`} className="d-flex gap-2 mb-2 align-items-start">
                    <Form.Control
                      size="sm"
                      placeholder={t("importTemplateRuleColumn")}
                      value={filter.column}
                      onChange={(e) => updateFilter(index, "column", e.target.value)}
                    />
                    <Form.Control
                      size="sm"
                      placeholder={t("importTemplateRuleValue")}
                      value={filter.value}
                      onChange={(e) => updateFilter(index, "value", e.target.value)}
                    />
                    <Button variant="outline-danger" size="sm" type="button" onClick={() => removeFilter(index)}>
                      <AiOutlineDelete />
                    </Button>
                  </div>
                ))
              )}
            </Form.Group>

            <Form.Group className="mb-0">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0">{t("importTemplateVatRules")}</Form.Label>
                <Button variant="outline-secondary" size="sm" type="button" onClick={addVatRule}>
                  <AiOutlinePlus className="me-1" />
                  {t("importTemplateAddRule")}
                </Button>
              </div>
              <Form.Text className="text-muted d-block mb-2">{t("importTemplateVatRulesHint")}</Form.Text>
              {form.vat_rules.length === 0 ? (
                <div className="text-muted small">{t("importTemplateNoRules")}</div>
              ) : (
                form.vat_rules.map((rule, index) => (
                  <div key={`vat-${index}`} className="d-flex gap-2 mb-2 align-items-start">
                    <Form.Control
                      size="sm"
                      placeholder={t("importTemplateRuleColumn")}
                      value={rule.column}
                      onChange={(e) => updateVatRule(index, "column", e.target.value)}
                    />
                    <Form.Control
                      size="sm"
                      placeholder={t("importTemplateRuleValue")}
                      value={rule.value}
                      onChange={(e) => updateVatRule(index, "value", e.target.value)}
                    />
                    <Form.Control
                      size="sm"
                      style={{ maxWidth: 90 }}
                      placeholder={t("importTemplateVatRate")}
                      value={rule.vat_rate}
                      onChange={(e) => updateVatRule(index, "vat_rate", e.target.value)}
                      inputMode="decimal"
                    />
                    <Button variant="outline-danger" size="sm" type="button" onClick={() => removeVatRule(index)}>
                      <AiOutlineDelete />
                    </Button>
                  </div>
                ))
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="import-template-modal__footer flex-shrink-0 d-flex gap-2 justify-content-end">
            <Button variant="secondary" type="button" onClick={() => setModalShow(false)} disabled={saving}>
              {t("cancel")}
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : t("save")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default ImportTemplates;
